import { useCallback, useEffect, useState } from "react";
import { api, eur, fmtDate } from "../lib/api";
import { PageTitle, StatusBadge, Chip, SearchBox, matchQ, focusRing } from "../components/common";
import { useFocusParam, useScrollTo } from "../hooks/useFocusParam";
import ControlPanel from "../components/ControlPanel";
import { DateRange, monthStart, today, rangeParams } from "../components/DateRange";
import { CONTROL_STATES, SERVICE_TYPES } from "../lib/labels";
import { useLang } from "../lib/i18n";

export default function Controls() {
  const { t, tr } = useLang();
  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState("");
  const [companies, setCompanies] = useState([]);
  const [decls, setDecls] = useState([]);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const focus = useFocusParam(["did", "pid"]);
  useEffect(() => {
    if (focus.did && focus.pid) setOpen({ did: focus.did, pid: focus.pid });
  }, [focus.did, focus.pid]);
  const load = useCallback(() => api.get("/declarations", { params: { ...rangeParams(range), company_id: company || undefined } }).then((r) => setDecls(r.data)), [range, company]);
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const rows = decls.flatMap((d) => d.prestations.map((p) => ({ d, p }))).filter((x) => !status || x.p.control_status === status)
    .filter(({ d, p }) => matchQ(q, p.ref, d.company_name, d.driver_name, d.employee_id, d.route_id));
  useScrollTo(focus.pid, rows.length);
  return (
    <div>
      <PageTitle title={t("ctl.title")} sub={t("ctl.sub")}>
        <SearchBox value={q} onChange={setQ} testid="controls-search" />
        <DateRange value={range} onChange={setRange} testid="controls-range" />
        <select className="tl-select w-48" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="controls-company">
          <option value="">{t("c.allCompanies")}</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="tl-select w-48" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="controls-status">
          <option value="">{t("c.allStates")}</option>
          {Object.entries(CONTROL_STATES).map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}
        </select>
      </PageTitle>
      <div className="tl-card overflow-x-auto">
        <table className="tl-table" data-testid="controls-table">
          <thead><tr>{["ref", "date", "compDriver", "missions", "ups", "sub2", "state", "alerts"].map((k) => <th key={k}>{t(`ctl.${k}`)}</th>)}</tr></thead>
          <tbody>
            {rows.map(({ d, p }) => (
              <tr key={p.id} data-focus-id={p.id} className={`cursor-pointer ${focusRing(focus.pid === p.id)} ${p.components.some((c) => c.type === "pm") ? "text-red-700 bg-red-50" : ""}`} onClick={() => setOpen({ did: d.id, pid: p.id })} data-testid={`control-row-${p.ref}`}>
                <td className="tl-mono">{p.ref}</td>
                <td>{fmtDate(d.work_date)}{d.day_type === "samedi" && <div><Chip tone="blue">samedi</Chip></div>}</td>
                <td>{d.company_name}<div className="text-xs text-slate-500">{d.driver_name}</div></td>
                <td>{p.components.map((c) => tr(SERVICE_TYPES[c.type])).join(" + ")}</td>
                <td className="tl-mono">{eur(p.calc.ups.known_total)}{p.calc.ups.status !== "complet" && <div className="text-amber-700 text-xs">{t("ctl.tbc")}</div>}</td>
                <td className="tl-mono">{eur(p.calc.sub.known_total)}{p.calc.sub.status !== "complet" && <div className="text-amber-700 text-xs">{t("ctl.tbc")}</div>}</td>
                <td><StatusBadge status={p.control_status} testid={`row-status-${p.ref}`} /></td>
                <td className="text-xs text-red-700">{[...(d.flags || []), ...(p.flags || [])].length || ""}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="text-slate-500">{t("ctl.none")}</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <ControlPanel {...open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}
